import React, { useState, useCallback, useRef, useEffect, memo } from 'react';
import { useSnackbar } from 'notistack';
import { Table, Box, TableBody, TableCell, TableContainer, TableHead, Typography, TableRow, Paper, IconButton, Tooltip, Button } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddDerateModal from './AddDerateModal';
import ImportRatioDerateModal from './ImportRatioDerateModal';
import { NoRecordFound, gaTelesisInstance } from '../../shared_elements';
import { regexConstants } from '../../constants/regEx';
import { exportToExcel } from '../../utils/helper';
import { numberWithCommas } from '../../utils';
import { PermissionWrapper } from '../../wrappers';

const PAGE_PERMISSION_FEATURE = { Reserve: 'reserve_rates', Rental: 'lease_rental_rate' };

const LiteCellInput = memo(function LiteCellInput({ value, onChange, onFocus, disabled, error, helperText, maxLength, startAdornment, width }) {
  return (
    <div style={{ width: width || '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, border: `1px solid ${error ? '#d32f2f' : '#c4c4c4'}`, borderRadius: 4, padding: '4px 8px', backgroundColor: disabled ? '#f5f5f5' : '#fff' }}>
        {startAdornment ? <span style={{ fontSize: 13, color: 'rgba(0,0,0,0.6)' }}>{startAdornment}</span> : null}
        <input
          value={value}
          onChange={onChange}
          onFocus={onFocus}
          disabled={disabled}
          maxLength={maxLength}
          style={{ border: 'none', outline: 'none', width: '100%', fontSize: 13, background: 'transparent', fontFamily: 'inherit', cursor: disabled ? 'not-allowed' : 'text' }}
        />
      </div>
      {error && helperText ? <div style={{ color: '#d32f2f', fontSize: 11, marginTop: 2 }}>{helperText}</div> : null}
    </div>
  );
});

// Memoized per-cell components are what actually stop typing lag at the PRD's max grid size.
const RatioCell = memo(function RatioCell({ rowIndex, value, disabled, error, helperText, allowDecimal, onRatioChange, onFocusCell }) {
  const handleChange = useCallback((e) => {
    regexConstants.decimalNoDotStartWithVariables(allowDecimal).test(e.target.value)
      ? onRatioChange(rowIndex, e.target.value)
      : e.preventDefault();
  }, [rowIndex, allowDecimal, onRatioChange]);
  const handleFocus = useCallback(() => onFocusCell(`ratio_row_${rowIndex}`), [rowIndex, onFocusCell]);

  return (
    <LiteCellInput
      disabled={disabled}
      value={value}
      error={error}
      helperText={helperText}
      onFocus={handleFocus}
      onChange={handleChange}
      maxLength={11}
    />
  );
});

const RateCell = memo(function RateCell({ rowIndex, colIndex, value, disabled, error, helperText, startAdornment, onRateChange, onFocusCell }) {
  const handleChange = useCallback((e) => {
    regexConstants.decimalnodotstart.test(e.target.value)
      ? onRateChange(colIndex, rowIndex, e.target.value)
      : e.preventDefault();
  }, [colIndex, rowIndex, onRateChange]);
  const handleFocus = useCallback(() => onFocusCell(`${colIndex}_${rowIndex}`), [colIndex, rowIndex, onFocusCell]);

  return (
    <LiteCellInput
      error={error}
      helperText={helperText}
      onFocus={handleFocus}
      disabled={disabled}
      value={value}
      onChange={handleChange}
      maxLength={11}
      startAdornment={startAdornment}
    />
  );
});

const DerateBasedRatioTable = ({ ratioDerateData, setRatioDerateData, modeType, page, setError, error, handleItemClicked }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [derateColModal, setDerateColModal] = useState({ addModal: false, derate: null, error: "" });
  const [importModalOpen, setImportModalOpen] = useState(false);
  const interval_condition = ratioDerateData?.interval_condition || [];
  const allRatios = interval_condition[0]?.ratios || [];
  const tableContainerRef = useRef(null);
  const prevRatioCountRef = useRef(allRatios.length);
  const columnHeaderRefs = useRef({});
  const pendingScrollDerateRef = useRef(null);

  useEffect(() => {
    if (allRatios.length > prevRatioCountRef.current && tableContainerRef.current) {
      tableContainerRef.current.scrollTop = tableContainerRef.current.scrollHeight;
    }
    prevRatioCountRef.current = allRatios.length;
  }, [allRatios.length]);

  // A newly added DeRate column can land anywhere once sorted into ascending order (not just at
  // the far right), so scroll it into view instead of relying on it always being at the edge.
  useEffect(() => {
    const targetDerate = pendingScrollDerateRef.current;
    if (targetDerate == null) return;
    const node = columnHeaderRefs.current[targetDerate];
    if (node) { node.scrollIntoView({ block: 'nearest', inline: 'center' }); }
    pendingScrollDerateRef.current = null;
  }, [interval_condition.length]);

  const handleFocusChange = useCallback((key) => {
    setError(prev => ({ ...prev, [key]: "" }));
  }, [setError]);

  const handleRateChange = useCallback((derateIndex, ratioIndex, value) => {
    setRatioDerateData(prev => {
      const current_ic = prev?.interval_condition || [];
      const updated = current_ic.map((row, i) => {
        if (i !== derateIndex) return row;
        return { ...row, ratios: row.ratios.map((r, j) => j === ratioIndex ? { ...r, rate: value } : r) };
      });
      return { ...prev, interval_condition: updated };
    });
  }, [setRatioDerateData]);

  const handleDeleteRow = (rowIndex) => {
    setRatioDerateData(prev => {
      const updated = (prev?.interval_condition || []).filter((_, i) => i !== rowIndex);
      return { ...prev, interval_condition: updated };
    });
    setError({});
  };

  const handleRatioChange = useCallback((ratioIndex, newValue) => {
    setRatioDerateData(prev => {
      const updated = (prev?.interval_condition || []).map(row => ({
        ...row,
        ratios: row.ratios.map((r, i) => i === ratioIndex ? { ...r, ratio: newValue } : r)
      }));
      return { ...prev, interval_condition: updated };
    });
  }, [setRatioDerateData]);

  const handleDeleteRatio = (ratioIndex) => {
    setRatioDerateData(prev => {
      const updated = (prev?.interval_condition || []).map(row => ({ ...row, ratios: row.ratios.filter((_, i) => i !== ratioIndex) }));
      return { ...prev, interval_condition: updated };
    });
    setError({});
  };

  const onDerateColFieldChange = (key, value) => { setDerateColModal(prev => ({ ...prev, [key]: value, error: "" })); };
  const onDerateColFocusChange = () => { setDerateColModal(prev => ({ ...prev, error: null })); };

  const handleAddRatioRow = () => {
    const rangeConditionErrors = {};
    const rangeConditionFields = [];
    if (!ratioDerateData?.interval) { rangeConditionErrors.interval = `Please select ${page} Rate interval`; rangeConditionFields.push(`${page} Rate Interval`); }
    if (!ratioDerateData?.allow_decimal) { rangeConditionErrors.allow_decimal = "Please select Allow Decimal"; rangeConditionFields.push("Allow Decimal"); }
    if (!ratioDerateData?.rounding) { rangeConditionErrors.rounding = "Please select Rounding"; rangeConditionFields.push("Rounding"); }
    if (rangeConditionFields.length > 0) {
      const fieldList = rangeConditionFields.length < 3 ? rangeConditionFields.join(' and ') : `${rangeConditionFields.slice(0, -1).join(', ')} and ${rangeConditionFields.at(-1)}`;
      enqueueSnackbar(`Please select ${fieldList} field(s).`, { variant: "error", anchorOrigin: { vertical: "top", horizontal: "right" } });
      setError(prev => ({ ...prev, ...rangeConditionErrors }));
      return;
    }
    if (interval_condition.length === 0) {
      enqueueSnackbar("Please add at least one DeRate before adding a Ratio.", { variant: "error", anchorOrigin: { vertical: "top", horizontal: "right" } });
      return;
    }
    setRatioDerateData(prev => {
      const current_ic = prev?.interval_condition || [];
      if (current_ic.length === 0) return prev;
      const updated = current_ic.map(row => ({ ...row, ratios: [...row.ratios, { ratio: "0", rate: "" }] }));
      return { ...prev, interval_condition: updated };
    });
  };

  const handleAddDerateCol = () => {
    const rangeConditionErrors = {};
    const rangeConditionFields = [];
    if (!ratioDerateData?.interval) { rangeConditionErrors.interval = `Please select ${page} Rate interval`; rangeConditionFields.push(`${page} Rate Interval`); }
    if (!ratioDerateData?.allow_decimal) { rangeConditionErrors.allow_decimal = "Please select Allow Decimal"; rangeConditionFields.push("Allow Decimal"); }
    if (!ratioDerateData?.rounding) { rangeConditionErrors.rounding = "Please select Rounding"; rangeConditionFields.push("Rounding"); }
    if (rangeConditionFields.length > 0) {
      const fieldList = rangeConditionFields.length < 3 ? rangeConditionFields.join(' and ') : `${rangeConditionFields.slice(0, -1).join(', ')} and ${rangeConditionFields.at(-1)}`;
      enqueueSnackbar(`Please select ${fieldList} field(s).`, { variant: "error", anchorOrigin: { vertical: "top", horizontal: "right" } });
      setError(prev => ({ ...prev, ...rangeConditionErrors }));
      return;
    }
    if (!derateColModal?.derate) { return setDerateColModal(prev => ({ ...prev, error: "DeRate value is required." })); }
    const rawValues = derateColModal.derate.split(",");
    if (rawValues.some(v => v.trim() === "")) { return setDerateColModal(prev => ({ ...prev, error: "Invalid comma placement" })); }
    const values = rawValues.map(v => parseFloat(v));
    if (values.some(v => v > 100)) { return setDerateColModal(prev => ({ ...prev, error: "DeRate must be equal or below 100%" })); }
    const duplicates = values.filter((v, i) => values.indexOf(v) !== i);
    if (duplicates.length) { return setDerateColModal(prev => ({ ...prev, error: `Duplicate values not allowed (${duplicates.join(",")})` })); }
    const existingDerates = interval_condition.map(row => Number(row.derate));
    const alreadyExists = values.filter(v => existingDerates.includes(v));
    if (alreadyExists.length) { enqueueSnackbar(`DeRate (${alreadyExists.join(",")}) already exists.`, { variant: "error", anchorOrigin: { vertical: "top", horizontal: "right" } }); return; }
    // Scroll the first newly added column into view once it renders at its sorted position.
    pendingScrollDerateRef.current = String(values[0]);
    setRatioDerateData(prev => {
      const current_ic = prev?.interval_condition || [];
      const currentRatios = current_ic[0]?.ratios || [];
      const newRows = values.map(v => ({ derate: String(v), type: null, ratios: currentRatios.map(r => ({ ratio: r.ratio, rate: "" })) }));
      // Keep DeRate columns in ascending order regardless of the order they were added in —
      // e.g. adding 56 into an existing 20,30,40,50,67 set should land as 20,30,40,50,56,67.
      const merged = [...current_ic, ...newRows].sort((a, b) => Number(a.derate) - Number(b.derate));
      return { ...prev, interval_condition: merged };
    });
    // Inserting a new column at its sorted position can shift every existing column after it —
    // unlike appending at the end, which never moved existing indices. Clear error state so no
    // stale "colIndex_rowIndex" error stays attached to the wrong column after the shift.
    setError({});
    setDerateColModal({ addModal: false, derate: null, error: "" });
  };

  const isButtonEnabled = !ratioDerateData?.interval?.value || !ratioDerateData?.allow_decimal?.value || !ratioDerateData?.rounding?.value;

  const formatImportedRate = (rate) => (rate === '' || rate === undefined ? '' : Number(rate).toFixed(2));

  const handleConfirmImport = ({ colDefs, parsedRows, mode }) => {
    setRatioDerateData(prev => {
      const current_ic = prev?.interval_condition || [];

      if (mode === 'replace') {
        const newIC = colDefs.map(cd => ({
          derate: String(cd.derate),
          type: null,
          ratios: parsedRows.map(pr => ({ ratio: pr.ratioStr, rate: formatImportedRate(pr.cells[cd.derate]) })),
        }));
        return { ...prev, interval_condition: newIC };
      }

      const existingRatioStrs = (current_ic[0]?.ratios || []).map(r => r.ratio);
      const existingRatioNums = existingRatioStrs.map(Number);
      const newRatioEntries = parsedRows.filter(pr => !existingRatioNums.includes(pr.ratio));
      const finalRatioOrder = [...existingRatioStrs, ...newRatioEntries.map(pr => pr.ratioStr)];

      const updatedIC = current_ic.map(row => {
        const matchingColDef = colDefs.find(cd => cd.derate === Number(row.derate));
        let ratios = row.ratios.map(r => {
          if (!matchingColDef) return r;
          const pr = parsedRows.find(p => p.ratio === Number(r.ratio));
          if (pr && pr.cells[matchingColDef.derate] !== '' && pr.cells[matchingColDef.derate] !== undefined) {
            return { ...r, rate: formatImportedRate(pr.cells[matchingColDef.derate]) };
          }
          return r;
        });
        newRatioEntries.forEach(pr => {
          const rate = matchingColDef ? formatImportedRate(pr.cells[matchingColDef.derate]) : '';
          ratios = [...ratios, { ratio: pr.ratioStr, rate }];
        });
        return { ...row, ratios };
      });

      const existingColNums = current_ic.map(row => Number(row.derate));
      const newColDefs = colDefs.filter(cd => !existingColNums.includes(cd.derate));
      const newColumns = newColDefs.map(cd => ({
        derate: String(cd.derate),
        type: null,
        ratios: finalRatioOrder.map(ratioStr => {
          // Match by numeric value, not string equality — an existing ratio's stored string
          // (e.g. "500.00") can differ textually from the import's normalized ratioStr (e.g. "500")
          // for the same numeric ratio, which would otherwise silently leave this cell blank.
          const pr = parsedRows.find(p => p.ratio === Number(ratioStr));
          return { ratio: ratioStr, rate: pr ? formatImportedRate(pr.cells[cd.derate]) : '' };
        }),
      }));

      return { ...prev, interval_condition: [...updatedIC, ...newColumns] };
    });
    setError({});
  };

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1, }} >
        <Typography className="section-header-text"> Ratio/DeRate Table </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button sx={{ pointerEvents: !ratioDerateData?.interval_condition?.length ? "not-allowed" : "pointer", }} disabled={!ratioDerateData?.interval_condition?.length || isButtonEnabled} size="small"
            onClick={() => {
              const formattedData = allRatios.map((ratioEntry, ratioIndex) => {
                const obj = { "Ratio / DeRate": ratioEntry.ratio };
                interval_condition.forEach((row) => {
                  const rateObj = row?.ratios?.[ratioIndex];
                  obj[`${row.derate}%`] = rateObj?.rate ? `${ratioDerateData?.currency?.symbol || "$"} ${rateObj.rate}` : "";
                });
                return obj;
              });
              exportToExcel({
                data: formattedData, fileName: "DeRate_Ratio_Based_Table", sheetName: "Ratio Table",
                ...(gaTelesisInstance ? {
                  dataCellStyle: (col) => col === 0
                    ? { font: { sz: 11, color: { rgb: "0000FF" } }, alignment: { horizontal: "center", vertical: "center" } }
                    : { font: { sz: 11, color: { rgb: "0000FF" } }, alignment: { horizontal: "right", vertical: "center" } },
                  headerCellStyle: { alignment: { horizontal: "center", vertical: "center" } },
                } : {}),
              });
              handleItemClicked?.('Export Button');
            }}
          >
            Export
          </Button>
          {modeType !== "view" && page === "Reserve" && gaTelesisInstance && (
            <PermissionWrapper extraProps={{ application: "lease_revamp", feature: PAGE_PERMISSION_FEATURE[page] || "reserve_rates", operationType: ['U'] }}>
              <Button sx={{ pointerEvents: isButtonEnabled ? "not-allowed" : "pointer" }} disabled={isButtonEnabled} size="small" onClick={() => { setImportModalOpen(true); handleItemClicked?.('Import Button') }} >
                Import
              </Button>
            </PermissionWrapper>
          )}
          {modeType !== "view" && (
            <Button variant="outlined" className="btn-primary" size="small" onClick={() => { setDerateColModal({ addModal: true, derate: null, error: "" }); handleItemClicked?.('Add DeRate Button') }} >
              Add DeRate
            </Button>
          )}
        </Box>
      </Box>
      <TableContainer ref={tableContainerRef} component={Paper} className="scrollable-table rental-scrollable-table-container" sx={{ maxHeight: 'min(420px, calc(100vh - 350px))', overflow: 'auto' }}>
        <Table size="small" className="mui-table-format" stickyHeader sx={{ tableLayout: 'fixed', minWidth: 300 + interval_condition.length * 110 }}>
          <TableHead>
            <TableRow>
              <>
                <TableCell
                  sx={{ borderRight: "1px solid #ccc", position: "sticky", left: 0, zIndex: 3, width: 140, backgroundColor: "#b5bedb" }}
                  style={{ height: 30 }}
                  className="nowrap-cell range-header"
                >
                  <b>Ratio / DeRate</b>
                </TableCell>
                {interval_condition.map((row, i) => (
                  <TableCell ref={(node) => { columnHeaderRefs.current[row.derate] = node; }} sx={{ borderRight: "1px solid #ccc", width: 140 }} key={i}>
                    <Box className="div-space-between" sx={{ alignItems: "center", gap: 1 }}>
                      <b>{row.derate}%</b>
                      {modeType !== "view" ? (
                        <Tooltip title="Delete">
                          <IconButton onClick={() => { handleDeleteRow(i); handleItemClicked?.('DeRate Column Delete Icon') }} size="small" color="error">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                    </Box>
                  </TableCell>
                ))}
                {modeType !== "view" ? (
                  <TableCell
                    align="center"
                    className='sticky-action-header'
                    sx={{ position: "sticky", right: 0, zIndex: 3, width: 56, backgroundColor: "#b5bedb" }}
                    style={{ height: "30px" }}
                  >
                    <b>Action</b>
                  </TableCell>
                ) : null}
              </>
            </TableRow>
          </TableHead>
          <TableBody>
            {allRatios.length > 0 && allRatios.map((ratioEntry, rowIndex) => (
              <tr key={rowIndex}>
                <TableCell
                  sx={{
                    borderRight: "1px solid #ccc",
                    position: "sticky",
                    left: 0,
                    zIndex: 1,
                    width: 140,
                    backgroundColor: `${rowIndex % 2 === 0 ? '#e6ebfb' : '#fff'} !important`,
                  }}
                >
                  <RatioCell
                    rowIndex={rowIndex}
                    value={ratioEntry.ratio}
                    disabled={modeType === "view"}
                    error={!!error[`ratio_row_${rowIndex}`]}
                    helperText={error[`ratio_row_${rowIndex}`] || ""}
                    allowDecimal={ratioDerateData?.allow_decimal?.value}
                    onRatioChange={handleRatioChange}
                    onFocusCell={handleFocusChange}
                  />
                </TableCell>
                {interval_condition.map((row, colIndex) => {
                  const rateObj = row?.ratios?.[rowIndex];
                  return (
                    <td style={{ borderRight: '1px solid #ccc', width: 140 }} key={colIndex}>
                      <RateCell
                        rowIndex={rowIndex}
                        colIndex={colIndex}
                        value={modeType === "view" && rateObj?.rate !== undefined && rateObj?.rate !== "" ? numberWithCommas(Number(rateObj.rate).toFixed(2)) : rateObj?.rate ?? ""}
                        disabled={modeType === "view"}
                        error={!!error[`${colIndex}_${rowIndex}`] || false}
                        helperText={error[`${colIndex}_${rowIndex}`] || ""}
                        startAdornment={ratioDerateData?.currency?.symbol}
                        onRateChange={handleRateChange}
                        onFocusCell={handleFocusChange}
                      />
                    </td>
                  );
                })}
                {modeType !== "view" ? (
                  <TableCell
                    align="center"
                    sx={{
                      borderRight: "1px solid #ccc",
                      p: 0,
                      width: 56,
                      position: "sticky",
                      right: 0,
                      zIndex: 1,
                      backgroundColor: `${rowIndex % 2 === 0 ? '#e6ebfb' : '#fff'} !important`,
                    }}
                    className="sticky-action-cell"
                  >
                    <Tooltip title="Delete">
                      <IconButton onClick={() => { handleDeleteRatio(rowIndex); handleItemClicked?.('Ratio Row Delete Icon') }} size="small" color="error">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                ) : null}
              </tr>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {allRatios.length === 0 && <NoRecordFound />}
      {modeType !== "view" ? (
        <Button sx={{ marginTop: 1, }} variant="outlined" className="btn-primary" size="small" onClick={() => { handleAddRatioRow(); handleItemClicked?.('Add Ratio Button') }}>
          Add Ratio
        </Button>) : null}
      {derateColModal?.addModal ? (
        <AddDerateModal
          ratioDerateData={ratioDerateData}
          derateData={derateColModal}
          onDerateFieldChange={onDerateColFieldChange}
          onDerateFocusChange={onDerateColFocusChange}
          onAddDerateColumn={handleAddDerateCol}
          derateModalData={derateColModal}
          onToggleModal={() => setDerateColModal({ addModal: false, derate: null, error: "" })}
          handleItemClicked={handleItemClicked}
        />
      ) : null}
      {importModalOpen ? (
        <ImportRatioDerateModal
          ratioDerateData={ratioDerateData}
          onToggleModal={() => setImportModalOpen(false)}
          onConfirmImport={handleConfirmImport}
          handleItemClicked={handleItemClicked}
        />
      ) : null}
    </>
  );
};

export default DerateBasedRatioTable;


